import { ReactNode } from "react";
import { Box, Button, CircularProgress, Link, Typography } from "@mui/material";
import inkfreeLogo from "assets/images/signer_inkfree_logo.svg";
import legacyInkfreeLogo from "assets/images/side_nav_inkfree_logo.svg";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { postSignSignupSearch } from "helpers/utm";
import history from "utils/history";
import { signerDoneGradient, signerDoneShadow } from "theme/colors";
import { getDisplaySignerEmail } from "screens/InPersonSigning/InPersonSigningHelper";
import SignerCard from "./SignerCard";
import { IS_EMBED } from "embed/embedMode";
import {
  CARD,
  CONTACT,
  FOOTER,
  INFO_STRIP,
  NOTIFY_PILL,
  REASON,
  STATUS_VISUALS,
  StatusIcon,
} from "./SignerStatusCard.tokens";

// White check glyph for the fully-signed card's gradient badge.
const SignedCheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} width={32} height={32}>
    <path d="M4.5 12.5l5 5L20 6.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Per-status badge glyph, traced 1:1 from `Signer Status Screens.dc.html`.
 * White stroke on the gradient circle; the caller sets the box size / stroke.
 */
const StatusGlyph = ({
  icon,
  size,
  stroke,
  color = "#fff",
}: {
  icon: StatusIcon;
  size: number;
  stroke: number;
  color?: string;
}) => {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: stroke,
    width: size,
    height: size,
  } as const;
  switch (icon) {
    case "clock":
      return (
        <svg {...common} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5V12l3 2" />
        </svg>
      );
    case "ban":
      return (
        <svg {...common} strokeLinecap="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M5.6 5.6l12.8 12.8" />
        </svg>
      );
    case "check":
      return (
        <svg {...common}>
          <path d="M4.5 12.5l5 5L20 6.5" />
        </svg>
      );
    case "x":
      return (
        <svg {...common} strokeLinecap="round">
          <path d="M18 6L6 18" />
          <path d="M6 6l12 12" />
        </svg>
      );
    case "personX":
      return (
        <svg {...common} strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M17 8l5 5" />
          <path d="M22 8l-5 5" />
        </svg>
      );
    case "refresh":
      return (
        <svg {...common} strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 2v6h-6" />
          <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
          <path d="M3 22v-6h6" />
          <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
        </svg>
      );
    case "list":
      return (
        <svg {...common} strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 6h13" />
          <path d="M8 12h13" />
          <path d="M8 18h13" />
          <path d="M3 6v.01" />
          <path d="M3 12v.01" />
          <path d="M3 18v.01" />
        </svg>
      );
    case "lock":
      return (
        <svg {...common} strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="10" width="16" height="10" rx="2" />
          <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          <circle cx="12" cy="15" r="1.4" fill={color} stroke="none" />
        </svg>
      );
    case "delegate":
      return (
        <svg {...common} strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 14l5-5-5-5" />
          <path d="M4 20v-7a4 4 0 0 1 4-4h12" />
        </svg>
      );
    default:
      return null;
  }
};

interface Props {
  status: string;
  isMobile: boolean;
  headerText?: string;
  bodyText?: string;
  downloadDocs: () => void;
  isDownloadingZip?: boolean;
  requesterName?: string;
  requesterEmail?: string;
  signedDocName?: string;
  signerName?: string;
  signerEmail?: string;
  /** Display request id (e.g. "#128") shown in the card footer. */
  requestId?: string;
  /** Reviewer's rejection reason, shown in the "YOUR REASON" callout. */
  reason?: string;
  /** Name of whoever declined/rejected (for the declined-card copy). */
  declinerName?: string;
  /** True when the current viewer is the one who declined/rejected. */
  declinedBySelf?: boolean;
  /** False hides the download button (inside the embed widget: the sender switched "Download documents" off). */
  allowDownload?: boolean;
}

/**
 * Terminal "Completion" card: signed / delegated / declined and the other
 * terminal states (expired, voided, waiting, rejected, verification locked,
 * approved, no-longer-associated, turn / order changed). Every colour and
 * spacing value comes from `SignerStatusCard.tokens.ts`.
 */
const SignerCompletionCard = ({
  status,
  isMobile,
  headerText,
  bodyText,
  downloadDocs,
  isDownloadingZip,
  requesterName,
  requesterEmail,
  signedDocName,
  signerName,
  signerEmail,
  requestId,
  reason,
  declinerName,
  declinedBySelf,
  allowDownload = true,
}: Props) => {
  // Fully-signed terminal state keeps its own richer card (download + sign-up
  // CTA) — it is not part of the status-cards redesign, so it is preserved.
  const isFullySigned = status === strings.signed;
  if (isFullySigned) {
    return (
      <FullySignedCard
        isMobile={isMobile}
        downloadDocs={downloadDocs}
        isDownloadingZip={isDownloadingZip}
        requesterName={requesterName}
        signedDocName={signedDocName}
        signerName={signerName}
        signerEmail={signerEmail}
        allowDownload={allowDownload}
      />
    );
  }

  const visual = STATUS_VISUALS[status];
  const copy = strings.getSignerStatusCardCopy(requesterName, {
    declinerName,
    declinedBySelf,
  })[status];
  const title = copy?.title ?? headerText;
  const body = copy?.body ?? bodyText;

  // Any status without a design entry (should not happen for terminal states)
  // falls back to the plain brand card so nothing renders blank.
  if (!visual) {
    return (
      <SignerCard maxWidth={CARD.width} sx={{ boxShadow: CARD.shadow }}>
        <CardBody title={title} body={body} bottomPad={36} />
      </SignerCard>
    );
  }

  const badge = (
    <Box
      aria-hidden
      sx={{
        width: CARD.circleSize,
        height: CARD.circleSize,
        borderRadius: "50%",
        background: visual.circle,
        boxShadow: visual.circleShadow,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mt: `${CARD.circleGap}px`,
      }}
    >
      <StatusGlyph
        icon={visual.icon}
        size={visual.iconSize}
        stroke={visual.iconStroke}
      />
    </Box>
  );

  const afterBody =
    visual.extra === "reason" && reason ? (
      <ReasonCallout reason={reason} />
    ) : visual.extra === "contact" && (requesterName || requesterEmail) ? (
      <ContactBox name={requesterName} email={requesterEmail} />
    ) : visual.extra === "notify" ? (
      <NotifyPill />
    ) : null;

  return (
    <SignerCard
      maxWidth={CARD.width}
      strip={visual.strip}
      sx={{ boxShadow: CARD.shadow }}
    >
      <CardBody
        title={title}
        body={body}
        bottomPad={visual.bottomPad}
        badge={badge}
        afterBody={afterBody}
      />

      {/* Footer strip. */}
      {visual.footer === "info" && <AwaitingStrip />}
      {visual.footer === "help" && (
        <FooterStrip requestId={requestId} requesterEmail={requesterEmail} showHelp />
      )}
      {visual.footer === "id" && <FooterStrip requestId={requestId} />}
    </SignerCard>
  );
};

/**
 * Shared body column: logo → badge → title → body → afterBody, all in one
 * centered, padded flex column (matches the design's card body).
 */
const CardBody = ({
  title,
  body,
  bottomPad,
  badge,
  afterBody,
}: {
  title?: string;
  body?: string;
  bottomPad: number;
  badge?: ReactNode;
  afterBody?: ReactNode;
}) => (
  <Box
    sx={{
      px: { xs: 3.5, sm: `${CARD.bodyPadX}px` },
      pt: `${CARD.bodyPadTop}px`,
      pb: `${bottomPad}px`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
    }}
  >
    <Box
      component="img"
      src={inkfreeLogo}
      alt="Inkfree"
      sx={{ width: CARD.logoWidth, height: "auto" }}
    />
    {badge}
    <Typography
      sx={{
        mt: `${CARD.titleGap}px`,
        fontSize: CARD.titleSize,
        fontWeight: 800,
        color: CARD.titleColor,
        letterSpacing: "-0.01em",
        lineHeight: 1.25,
      }}
    >
      {title}
    </Typography>
    {body && (
      <Typography
        sx={{
          mt: `${CARD.bodyGap}px`,
          fontSize: CARD.bodySize,
          lineHeight: CARD.bodyLineHeight,
          color: CARD.bodyColor,
          maxWidth: CARD.bodyMaxWidth,
        }}
      >
        {body}
      </Typography>
    )}
    {afterBody}
  </Box>
);

const NotifyPill = () => (
  <Box
    sx={{
      mt: `${NOTIFY_PILL.gap}px`,
      display: "inline-flex",
      alignItems: "center",
      gap: "9px",
      height: NOTIFY_PILL.height,
      px: `${NOTIFY_PILL.padX}px`,
      borderRadius: "999px",
      background: NOTIFY_PILL.bg,
      border: `1px solid ${NOTIFY_PILL.border}`,
    }}
  >
    <StatusGlyph icon="clock" size={NOTIFY_PILL.iconSize} stroke={2} color={NOTIFY_PILL.color} />
    <Typography sx={{ fontSize: NOTIFY_PILL.fontSize, fontWeight: 700, color: NOTIFY_PILL.color }}>
      {strings.signerNotifyPillLabel}
    </Typography>
  </Box>
);

const ReasonCallout = ({ reason }: { reason: string }) => (
  <Box
    sx={{
      mt: `${REASON.gap}px`,
      width: "100%",
      px: `${REASON.padX}px`,
      py: `${REASON.padY}px`,
      borderLeft: `3px solid ${REASON.bar}`,
      background: REASON.bg,
      borderRadius: "0 10px 10px 0",
      textAlign: "left",
      boxSizing: "border-box",
    }}
  >
    <Typography
      component="span"
      sx={{ fontSize: REASON.labelSize, fontWeight: 800, color: REASON.label, letterSpacing: "0.04em" }}
    >
      {strings.signerReasonLabel}
    </Typography>
    <Typography
      sx={{ mt: "6px", fontSize: REASON.textSize, color: REASON.text, lineHeight: 1.6, fontStyle: "italic" }}
    >
      {`"${reason}"`}
    </Typography>
  </Box>
);

const ContactBox = ({ name, email }: { name?: string; email?: string }) => (
  <Box
    sx={{
      mt: `${CONTACT.gap}px`,
      width: "100%",
      px: `${CONTACT.padX}px`,
      py: `${CONTACT.padY}px`,
      border: `1px solid ${CONTACT.border}`,
      borderRadius: "12px",
      background: CONTACT.bg,
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      textAlign: "left",
      boxSizing: "border-box",
    }}
  >
    <Typography sx={{ fontSize: CONTACT.fontSize, fontWeight: 700, color: CONTACT.title }}>
      {strings.signerContactRequesterTitle}
    </Typography>
    {name && (
      <Typography sx={{ fontSize: CONTACT.fontSize, color: CONTACT.name }}>{name}</Typography>
    )}
    {email && (
      <Link
        href={`mailto:${email}`}
        underline="none"
        sx={{ fontSize: CONTACT.fontSize, fontWeight: 600, color: "signer.orange.main" }}
      >
        {email}
      </Link>
    )}
  </Box>
);

const AwaitingStrip = () => (
  <Box
    sx={{
      borderTop: `1px solid ${INFO_STRIP.borderTop}`,
      background: INFO_STRIP.bg,
      px: { xs: 28 / 8, sm: `${CARD.bodyPadX}px` },
      pt: `${INFO_STRIP.padTop}px`,
      pb: `${INFO_STRIP.padBottom}px`,
      display: "flex",
      alignItems: "center",
      gap: `${INFO_STRIP.gap}px`,
    }}
  >
    <Box
      sx={{
        width: INFO_STRIP.chipSize,
        height: INFO_STRIP.chipSize,
        borderRadius: "50%",
        background: INFO_STRIP.chipBg,
        border: `1px solid ${INFO_STRIP.chipBorder}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <StatusGlyph icon="clock" size={INFO_STRIP.chipIconSize} stroke={2} color={INFO_STRIP.chipIcon} />
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", textAlign: "left" }}>
      <Typography sx={{ fontSize: INFO_STRIP.titleSize, fontWeight: 700, color: INFO_STRIP.titleColor }}>
        {strings.signerAwaitingTitle}
      </Typography>
      <Typography sx={{ fontSize: INFO_STRIP.subSize, color: INFO_STRIP.subColor }}>
        {strings.signerAwaitingSubtitle}
      </Typography>
    </Box>
  </Box>
);

const FooterStrip = ({
  requestId,
  requesterEmail,
  showHelp,
}: {
  requestId?: string;
  requesterEmail?: string;
  showHelp?: boolean;
}) => (
  <Box
    sx={{
      px: { xs: 28 / 8, sm: `${CARD.bodyPadX}px` },
      py: `${FOOTER.padY}px`,
      borderTop: `1px solid ${FOOTER.borderTop}`,
      background: FOOTER.bg,
      display: "flex",
      alignItems: "center",
      justifyContent: showHelp ? "space-between" : "center",
    }}
  >
    {showHelp && (
      <Typography sx={{ fontSize: FOOTER.fontSize, color: FOOTER.text }}>
        {strings.signerNeedHelpPrefix}{" "}
        <Link
          href={requesterEmail ? `mailto:${requesterEmail}` : "#"}
          underline="none"
          sx={{ fontWeight: 600, color: "signer.orange.main" }}
        >
          {strings.signerContactSupportLabel}
        </Link>
      </Typography>
    )}
    {requestId && (
      <Typography sx={{ fontSize: FOOTER.fontSize, color: FOOTER.text }}>
        {strings.signerRequestIdPrefix} {requestId}
      </Typography>
    )}
  </Box>
);

/* -------------------------------------------------------------------------- */
/* Fully-signed card (preserved from the previous implementation, unchanged   */
/* in look). Not part of the status-cards redesign.                           */
/* -------------------------------------------------------------------------- */

const FullySignedCard = ({
  isMobile,
  downloadDocs,
  isDownloadingZip,
  requesterName,
  signedDocName,
  signerName,
  signerEmail,
  allowDownload,
}: {
  isMobile: boolean;
  downloadDocs: () => void;
  isDownloadingZip?: boolean;
  requesterName?: string;
  signedDocName?: string;
  signerName?: string;
  signerEmail?: string;
  allowDownload: boolean;
}) => {
  // An in-person signer may have given no email, in which case nothing is
  // delivered to them and promising an inbox would be plainly untrue. Their
  // placeholder address is never a real one, so it also must not prefill the
  // sign-up form below.
  const deliverableEmail = getDisplaySignerEmail(signerEmail);
  const displayBody = `${
    deliverableEmail
      ? `A completed copy of ${
          signedDocName || "your document"
        } is on its way to your inbox.`
      : `${signedDocName || "Your document"} has been signed.`
  }${
    requesterName ? ` ${requesterName} has been notified that you signed.` : ""
  }`;

  return (
    <SignerCard maxWidth={520}>
      <Box
        sx={{
          px: { xs: 3, sm: 6 },
          pt: { xs: 3.5, sm: 5.5 },
          pb: { xs: 3.5, sm: 5 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Box
          component="img"
          src={legacyInkfreeLogo}
          alt="Inkfree"
          sx={{ width: isMobile ? 88 : 104 }}
        />

        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: signerDoneGradient.signed,
            color: "common.white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 3.75,
            boxShadow: signerDoneShadow.signed,
          }}
        >
          <SignedCheckIcon />
        </Box>

        <Typography
          sx={{
            mt: 3,
            fontSize: isMobile ? "1.25rem" : "1.5rem",
            fontWeight: 800,
            color: "signer.ink",
            letterSpacing: "-0.01em",
          }}
        >
          You're all signed!
        </Typography>

        <Typography
          sx={{
            mt: 1.25,
            fontSize: "0.84375rem",
            lineHeight: 1.65,
            color: "signer.text.meta",
            maxWidth: 380,
          }}
        >
          {displayBody}
        </Typography>

        {/* Inside the embed widget the sender's "Download documents" permission decides. */}
        {allowDownload && (
        <Button
          onClick={downloadDocs}
          disabled={isDownloadingZip}
          startIcon={
            isDownloadingZip ? (
              <CircularProgress size={16} thickness={5} sx={{ color: "common.white" }} />
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" width={16} height={16}>
                <path d="M12 3v12" />
                <path d="M7 10l5 5 5-5" />
                <path d="M4 20h16" />
              </svg>
            )
          }
          sx={{
            mt: 3.25,
            height: 46,
            px: 3.5,
            borderRadius: "10px",
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 700,
            color: "common.white",
            backgroundColor: "primary.main",
            boxShadow: "none",
            "& .MuiButton-startIcon": { mr: "9px", ml: 0 },
            "&:hover": { backgroundColor: "primary.dark", boxShadow: "none" },
          }}
        >
          {isDownloadingZip ? "Preparing download…" : "Download signed copy"}
        </Button>
        )}
      </Box>

      {/* The Inkfree sign-up pitch does not belong inside a partner's own app. */}
      {!IS_EMBED && (
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "signer.border.main",
          backgroundColor: "signer.surface.subtle",
          px: { xs: 3, sm: 6 },
          pt: { xs: 2.75, sm: 3.25 },
          pb: { xs: 3, sm: 3.75 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 800, color: "signer.ink" }}>
          Keep this document — and send your own
        </Typography>
        <Typography
          sx={{
            mt: 0.75,
            fontSize: "0.78125rem",
            lineHeight: 1.6,
            color: "signer.text.meta",
            maxWidth: 380,
          }}
        >
          Create a free Inkfree account to keep every document you sign in your
          inbox, and send documents out for signature yourself.
        </Typography>
        <Box
          sx={{
            mt: 1.75,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: 2.25,
            color: "signer.purple.main",
          }}
        >
          {["Docs saved to your inbox", "Send for signing to anyone"].map((feature) => (
            <Box key={feature} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.6}
                strokeLinecap="round"
                strokeLinejoin="round"
                width={13}
                height={13}
              >
                <path d="M4.5 12.5l5 5L20 6.5" />
              </svg>
              <Typography sx={{ fontSize: "0.71875rem", fontWeight: 700, color: "inherit" }}>
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>
        <Button
          onClick={() =>
            history.push({
              pathname: viewpaths.registerViewPath,
              search: postSignSignupSearch(strings.UTM_CONTENT_REGISTER_CTA),
              state: {
                prefillName: signerName,
                prefillEmail: deliverableEmail || undefined,
              },
            })
          }
          variant="outlined"
          sx={{
            mt: 2.25,
            height: 42,
            px: 3.25,
            borderRadius: "10px",
            textTransform: "none",
            fontSize: "0.84375rem",
            fontWeight: 700,
            color: "signer.purple.main",
            borderColor: "signer.purple.main",
            borderWidth: "1.5px",
            "&:hover": {
              borderWidth: "1.5px",
              borderColor: "signer.purple.main",
              backgroundColor: "signer.surface.muted",
            },
          }}
        >
          Sign up free
        </Button>
        <Typography sx={{ mt: 1.25, fontSize: "0.6875rem", color: "signer.text.muted" }}>
          Free forever · No credit card required
        </Typography>
      </Box>
      )}
    </SignerCard>
  );
};

export default SignerCompletionCard;
